import DieCode, { type DieTerm } from "@/types/die-code";
import DieAdjustment from "@/types/die-adjustment";
import DieRank from "@/types/die-rank";

type DiceTerm = Extract<DieTerm, { kind: 'dice' }>;

function diceTerm(sides: number, count: number = 1, sign: 1 | -1 = 1): DiceTerm {
    return { kind: 'dice', sign, count, sides, multiplier: 1 };
}

/**
 * Advance a die term by `steps` ranks using DieRank's ladder.
 *
 * DieRank folds overflow past d12 into its own modifier, so `d10` advanced 2 ranks is `d12` plus a
 * modifier of 1. Read the sides and the modifier SEPARATELY — never getDieCode(), which joins them
 * into `'d12+1'`. Rendering that string as a term double-counts the `+1`.
 */
function advance(term: DiceTerm, steps: number): { term: DiceTerm; carry: number } {
    if (steps === 0) return { term, carry: 0 };

    const rank = new DieRank(`d${term.sides}`);
    if (steps > 0) rank.addSteps(steps);
    else rank.subtractSteps(Math.abs(steps));

    return {
        term: { ...term, sides: rank.getDieSides() ?? term.sides },
        carry: rank.getDieModifier() ?? 0,
    };
}

/**
 * A value that is not a usable number contributes nothing. SwadeModConfigurationInterface.value
 * may legally hold a currency symbol or a UUID, and `Number()` turns those into NaN — which would
 * render as the literal string "NaN", re-parse as an opaque token, and blank the whole formula.
 * One malformed modification must not do that.
 *
 * The guard is deliberately narrower than `isFinite`. A blank or whitespace-only string coerces to
 * a finite 0, which inside a product stamps `x0` on the leading term — the same corruption in a
 * quieter form. Non-integers are rejected because `renderTerms` emits them verbatim while
 * `DieCode`'s FLAT_PATTERN is `/^\d+$/`, so `2d6x0.5` fails to re-parse and the formula silently
 * flips to unparsed with a zeroed numeric API.
 *
 * `allowNegative` is false only for the MULTIPLIER, which is the one call site that renders the
 * value into the formula: `2d6x-2` is split on the `-` by the parser and degrades to a token. The
 * additive accumulators never render it — the carry fold re-derives sign from the running total —
 * so a negative is safe there, and rejecting it would silently no-op `{add, -2, modify}`.
 *
 * The fallback is the caller's IDENTITY: 0 for the additive accumulators, 1 for the multiplicative.
 */
function numericValue(
    value: string | number | null,
    fallback: number = 0,
    allowNegative: boolean = true,
): number {
    if (value === null) return fallback;
    if (typeof value === 'string' && value.trim() === '') return fallback;
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return fallback;
    if (!allowNegative && parsed < 0) return fallback;
    return parsed;
}

function largestDiceIndex(terms: DieTerm[]): number {
    let best = -1;
    terms.forEach((term, index) => {
        if (term.kind !== 'dice') return;
        // Strict `>` keeps the first term on a tie.
        if (best < 0 || term.sides > (terms[best] as DiceTerm).sides) best = index;
    });
    return best;
}

/**
 * A die roll: one base DieCode plus the adjustments applied to it.
 *
 * DieRoll holds no formula grammar — parsing and rendering live in DieCode, ladder math lives in
 * DieRank. What lives here is the accumulation pipeline, and every accessor delegates to the
 * DieCode it resolves to.
 */
export default class DieRoll {
    private base: DieCode;
    private adjustments: DieAdjustment[] = [];

    constructor(value: string) {
        this.base = new DieCode(value);
    }

    public add(value: DieAdjustment | string): DieRoll {
        const adjustment = value instanceof DieAdjustment ? value : new DieAdjustment(value);
        if (adjustment.isParsed()) this.adjustments.push(adjustment);
        return this;
    }

    /**
     * Three ordered stages: dice, then multiplier, then appends. Recomputed on every call and pure
     * in (base, adjustments), so repeated calls are stable and adjustment order is irrelevant.
     */
    protected resolve(): DieCode {
        // Fast path — with no adjustments the base IS the result, which is what preserves
        // getNotation()'s verbatim-input contract for the random-table consumers.
        if (this.adjustments.length === 0) return this.base;

        const terms: DieTerm[] = this.base.getTerms();
        let carry = 0;

        // Stage 1a — extra-die track. Count first, then apply once: N codes select or create one
        // die, and creation spends the first step. This is what makes Claws + Brawler yield
        // `Str+d6` rather than `Str+d6+d4`.
        const trackCodes = this.adjustments.filter(
            (adjustment) => adjustment.getValueType() === 'step'
                && adjustment.getDie() !== null
                // Stage 2 owns every multiply, same as stage 1b and stage 3. Without this,
                // `multiply d4 step` is silently reinterpreted as `add d4 step`.
                && adjustment.getOperator() !== 'multiply',
        );

        if (trackCodes.length > 0) {
            // Seed from the SMALLEST die among the track codes, not the first one added. Seeding
            // by insertion order made a mixed-size track order-dependent: on base `Str+d6`,
            // ['d6 step','d4 step'] gave `Str+d10` while ['d4 step','d6 step'] gave `Str+d6+d6`.
            const seed = trackCodes
                .map((adjustment) => adjustment.getDie()!.getTerms()[0] as DiceTerm)
                .reduce((smallest, term) => (term.sides < smallest.sides ? term : smallest));
            const steps = trackCodes.reduce((total, adjustment) => total + adjustment.getSign(), 0);

            if (steps > 0) {
                // Only a count-of-1 term is eligible; a group such as `2d4` is left untouched.
                const index = terms.findIndex(
                    (term) => term.kind === 'dice' && term.count === 1 && term.sides === seed.sides,
                );

                if (index >= 0) {
                    const advanced = advance(terms[index] as DiceTerm, steps);
                    terms[index] = advanced.term;
                    carry += advanced.carry;
                } else {
                    const advanced = advance(diceTerm(seed.sides), steps - 1);
                    terms.push(advanced.term);
                    carry += advanced.carry;
                }
            }
        }

        // Stage 1b — numeric steps, applied to the formula as it stands after the track.
        const numericSteps = this.adjustments
            .filter((adjustment) => adjustment.getValueType() === 'step'
                && adjustment.getDie() === null
                // Stage 2 owns every multiply. Without this an operator:'multiply',
                // valueType:'step' adjustment would fire here AND there.
                && adjustment.getOperator() !== 'multiply')
            .reduce((total, adjustment) => total + (adjustment.getSign() * numericValue(adjustment.getValue())), 0);

        if (numericSteps !== 0) {
            let index = largestDiceIndex(terms);
            let steps = numericSteps;

            if (index < 0 && numericSteps > 0) {
                terms.push(diceTerm(4));
                index = terms.length - 1;
                steps = numericSteps - 1;
            }

            if (index >= 0) {
                const advanced = advance(terms[index] as DiceTerm, steps);
                terms[index] = advanced.term;
                carry += advanced.carry;
            }
        }

        // Stage 2 — multiplier, on the leading term only.
        const multiplier = this.adjustments
            .filter((adjustment) => adjustment.getOperator() === 'multiply')
            .reduce((product, adjustment) => product * numericValue(adjustment.getValue(), 1, false), 1);

        const leading = terms[0];
        if (multiplier !== 1 && leading !== undefined) {
            // A flat term carries no multiplier field — DieCode folds a multiplied bare number
            // into its value at parse time — so scale the value instead of skipping the stage.
            terms[0] = leading.kind === 'flat'
                ? { ...leading, value: leading.value * multiplier }
                : { ...leading, multiplier: leading.multiplier * multiplier };
        }

        // Stage 3 — appends. Dice modifies merge by die size; numeric ones fold into the carry.
        const modifies = this.adjustments
            .filter((adjustment) =>
                adjustment.getValueType() === 'modify' && adjustment.getOperator() !== 'multiply')
            // Appends land in the term list in the order they are processed, so an unsorted list
            // makes getFormula() depend on the order adjustments were added. Sort by sign, then
            // die size, then count — numeric modifies (no die) sort last and fold into the carry.
            .sort((a, b) => {
                const dieA = a.getDie()?.getTerms()[0];
                const dieB = b.getDie()?.getTerms()[0];
                if (!dieA && !dieB) return 0;
                if (!dieA) return 1;
                if (!dieB) return -1;
                if (a.getSign() !== b.getSign()) return b.getSign() - a.getSign();
                if (dieA.kind === 'dice' && dieB.kind === 'dice') {
                    if (dieA.sides !== dieB.sides) return dieA.sides - dieB.sides;
                    return dieA.count - dieB.count;
                }
                return 0;
            });

        for (const adjustment of modifies) {
            const die = adjustment.getDie();

            if (die === null) {
                carry += adjustment.getSign() * numericValue(adjustment.getValue());
                continue;
            }

            const source = die.getTerms()[0] as DiceTerm;
            const sign = adjustment.getSign();
            const existing = terms.find(
                (term) => term.kind === 'dice' && term.sides === source.sides && term.sign === sign,
            ) as DiceTerm | undefined;

            if (existing) existing.count += source.count;
            else terms.push(diceTerm(source.sides, source.count, sign));
        }

        if (carry !== 0) {
            // Fold into a flat term the base already carries so `Str+d6+2` plus `2 modify`
            // reads `Str+d6+4` rather than `Str+d6+2+2`.
            const flatIndex = terms.findIndex((term) => term.kind === 'flat');
            if (flatIndex >= 0) {
                const flat = terms[flatIndex] as Extract<DieTerm, { kind: 'flat' }>;
                const total = (flat.sign * flat.value) + carry;
                if (total === 0) terms.splice(flatIndex, 1);
                else terms[flatIndex] = { kind: 'flat', sign: total < 0 ? -1 : 1, value: Math.abs(total) };
            } else {
                terms.push({ kind: 'flat', sign: carry < 0 ? -1 : 1, value: Math.abs(carry) });
            }
        }

        return DieCode.fromTerms(terms);
    }

    public isParsed(): boolean {
        return this.resolve().isParsed();
    }

    public getNotation(): string | null {
        return this.resolve().getNotation();
    }

    public getFormula(): string {
        return this.resolve().getFormula();
    }

    public getMin(): number {
        return this.resolve().getMin();
    }

    public getMax(): number {
        return this.resolve().getMax();
    }

    public roll(): number {
        return this.resolve().roll();
    }
}
